import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchConditions } from "../../Redux/WebsiteSlice";
import { useBookingModal } from "../../context/BookingModalContext";
import { getImageUrl } from "../../services/api";
import PageAboutSection from "../Shared/PageAboutSection";
import Reveal from "../Shared/Reveal";
import s1 from "../../assets/img/s1.jpg";
import s2 from "../../assets/img/s2.png";
import s3 from "../../assets/img/s3.png";
import s4 from "../../assets/img/s4.jpg";
import s5 from "../../assets/img/s5.jpg";
import "./ConditionsPage.scss";

const CONDITION_FALLBACKS = [s1, s2, s3, s4, s5];

const defaultConditions = [
  {
    title: "Back Pain",
    description:
      "Lower and upper back pain is one of the most common musculoskeletal conditions. Our physiotherapists use hands-on treatment, exercise therapy, and education to address the root cause.",
  },
  {
    title: "Neck Pain",
    description:
      "Neck pain can result from poor posture, injury, or degeneration. We provide targeted treatment including joint mobilisation, soft tissue therapy, and personalised exercise programs.",
  },
  {
    title: "Shoulder Pain",
    description:
      "From rotator cuff tears to frozen shoulder, our team is experienced in diagnosing and treating the full range of shoulder conditions to restore strength and movement.",
  },
  {
    title: "Knee Pain",
    description:
      "Whether you have osteoarthritis, a ligament injury, or runner's knee, our physiotherapists can help you reduce pain and get back to the activities you love.",
  },
  {
    title: "Hip Pain",
    description:
      "Hip pain can significantly affect mobility and quality of life. We treat hip conditions including bursitis, labral tears, and post-surgical rehabilitation.",
  },
  {
    title: "Sports Injuries",
    description:
      "Sprains, strains, and overuse injuries are common in athletes of all levels. Our sports physiotherapists design structured return-to-sport programs tailored to your goals.",
  },
  {
    title: "Post-Surgical Rehabilitation",
    description:
      "Recovery after surgery requires expert guidance. We work closely with surgeons to provide evidence-based rehabilitation programs that optimise your recovery.",
  },
  {
    title: "Headaches & Migraines",
    description:
      "Cervicogenic headaches originating from the neck respond well to physiotherapy. We assess and treat the underlying cervical dysfunction contributing to your headaches.",
  },
  {
    title: "Arthritis",
    description:
      "Exercise and hands-on therapy are effective in managing arthritis symptoms. We help patients maintain function, reduce pain, and improve their quality of life.",
  },
  {
    title: "Sciatica",
    description:
      "Sciatica causes pain radiating down the leg and can be debilitating. Our treatment addresses the spinal cause and helps relieve nerve pain through targeted therapy.",
  },
  {
    title: "Foot & Ankle Pain",
    description:
      "From plantar fasciitis to ankle sprains, our physiotherapists provide effective assessment and treatment for all foot and ankle conditions.",
  },
  {
    title: "NDIS & Chronic Conditions",
    description:
      "We support NDIS participants and people with chronic conditions through tailored exercise therapy and physiotherapy programs to maximise independence and wellbeing.",
  },
];

const ConditionsPage = () => {
  const dispatch = useDispatch();
  const conditions = useSelector((state) => state.website.conditions);
  const { openModal } = useBookingModal();

  useEffect(() => {
    dispatch(fetchConditions());
  }, [dispatch]);

  const displayConditions = conditions && conditions.length > 0 ? conditions : defaultConditions;
  const [visibleCount, setVisibleCount] = useState(9);
  const visibleConditions = displayConditions.slice(0, visibleCount);
  const hasMore = visibleCount < displayConditions.length;

  return (
    <div className="page-conditions">
      <div className="page-hero">
        <div className="page-hero__content">
          <span className="small__text">WE TREAT</span>
          <h1 className="headline__text">
            Common <span>Conditions</span>
          </h1>
          <p className="page-hero__subtitle">
            Our physiotherapists are experienced in treating a wide range of musculoskeletal and chronic conditions.
          </p>
        </div>
      </div>

      <PageAboutSection
        pageKey="conditions"
        fallbackHeading="Conditions We Treat"
        fallbackParagraph="From acute injuries to chronic pain, our experienced physiotherapists diagnose and treat a wide range of musculoskeletal and chronic conditions with personalised, evidence-based care."
      />

      <div className="conditions-body">
        <div className="conditions-body__inner">

          <Reveal as="div" direction="up" className="conditions-grid">
            {visibleConditions.map((condition, i) => {
              const fallback = CONDITION_FALLBACKS[i % CONDITION_FALLBACKS.length];
              const cardContent = (
                <>
                  <div className="condition-card__image">
                    <img
                      src={condition.image ? getImageUrl(condition.image) : fallback}
                      alt={condition.title}
                      onError={(e) => { e.target.src = fallback; }}
                    />
                  </div>
                  <div className="condition-card__content">
                    <h3 className="condition-card__title">{condition.title}</h3>
                    {condition.description && (
                      <p className="condition-card__desc">{condition.description}</p>
                    )}
                    {condition._id && <span className="condition-card__link">Learn more &rarr;</span>}
                  </div>
                </>
              );

              return condition._id ? (
                <Link key={condition._id} to={`/conditions/${condition.slug || condition._id}`} className="condition-card">
                  {cardContent}
                </Link>
              ) : (
                <div key={i} className="condition-card">
                  {cardContent}
                </div>
              );
            })}
          </Reveal>

          {hasMore && (
            <div className="conditions-load-more">
              <button
                type="button"
                className="site__button site__button_style--solid site__button_shape--default"
                onClick={() => setVisibleCount(c => c + 9)}
              >
                <span className="text">Load More</span>
              </button>
            </div>
          )}

        </div>
      </div>

      <div className="conditions-footer-cta">
        <div className="conditions-footer-cta__inner">
          <Reveal as="div" direction="left" className="conditions-footer-cta__text">
            <span className="small__text">NOT SURE WHERE TO START?</span>
            <h2>We're here to help you recover</h2>
            <p>Book an assessment and our physiotherapists will diagnose your condition and build a personalised plan.</p>
          </Reveal>
          <Reveal as="div" direction="right" delay={0.1}>
            <button
              type="button"
              onClick={() => setTimeout(openModal, 0)}
              className="site__button site__button_style--solid site__button_shape--default"
            >
              <span className="text">Book an Appointment</span>
            </button>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default ConditionsPage;
